import React, { useState, useEffect } from 'react';
import { FileText, Download, Send, Calendar, TrendingUp, TrendingDown, Users, Clock, BarChart3, CheckCircle, XCircle, Banknote } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API, downloadPdf } from '@/utils/api';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const WeeklyReportPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    axios.get(`${API}/weekly-report/preview`).then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleDownload = () => {
    downloadPdf(`${API}/weekly-report/download`, `rapport_hebdo.pdf`);
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/weekly-report/send`);
      toast.success(`Rapport envoye a ${res.data.sent} destinataire(s)`);
    } catch {
      toast.error("Erreur lors de l'envoi");
    }
    setSending(false);
  };

  if (loading) return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-pulse text-slate-400">{t('common.loading')}</div>
      </div>
    </AdminLayout>
  );

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="p-4 md:p-6 space-y-6 max-w-[1000px] mx-auto" data-testid="weekly-report-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white font-heading">Rapport Hebdomadaire</h1>
            {data && <p className="text-sm text-slate-500 mt-0.5">Semaine du {data.period_label}</p>}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleDownload} className="gap-1.5" data-testid="download-weekly-report">
              <Download className="w-3.5 h-3.5" />
              Telecharger PDF
            </Button>
            <Button size="sm" onClick={handleSend} disabled={sending} className="gap-1.5" data-testid="send-weekly-report">
              <Send className="w-3.5 h-3.5" />
              {sending ? 'Envoi...' : 'Envoyer par email'}
            </Button>
          </div>
        </div>

        {data && (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="weekly-kpi-cards">
              <KpiCard
                icon={Banknote}
                label="Chiffre d'affaires"
                value={`CHF ${data.ca_total.toLocaleString('fr-CH', { minimumFractionDigits: 0 })}`}
                variation={data.ca_variation}
                accent="emerald"
              />
              <KpiCard
                icon={CheckCircle}
                label="Fiches terminees"
                value={data.fiches_terminees}
                subtext={`${data.fiches_terminees_prev} sem. prec.`}
                accent="blue"
              />
              <KpiCard
                icon={BarChart3}
                label="Satisfaction"
                value={`${data.satisfaction_avg}/5`}
                subtext={`${data.satisfaction_count} avis`}
                accent="amber"
              />
              <KpiCard
                icon={TrendingUp}
                label="Marge nette"
                value={`CHF ${data.marge_nette.toLocaleString('fr-CH', { minimumFractionDigits: 0 })}`}
                accent={data.marge_nette >= 0 ? 'emerald' : 'red'}
                isNegative={data.marge_nette < 0}
              />
            </div>

            {/* Detail table */}
            <Card className="rounded-xl overflow-hidden" data-testid="weekly-detail-table">
              <div className="px-5 py-3 bg-slate-900 dark:bg-slate-800">
                <h3 className="text-sm font-bold text-white">Detail de la semaine</h3>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-slate-700">
                <DetailRow label="Fiches creees" value={data.fiches_creees} prev={data.fiches_creees_prev} />
                <DetailRow label="Fiches terminees" value={data.fiches_terminees} prev={data.fiches_terminees_prev} />
                <DetailRow label="Devis envoyes" value={data.devis_total} />
                <DetailRow label="Devis acceptes" value={data.devis_acceptes} badge={data.devis_total > 0 ? `${Math.round(data.devis_acceptes / data.devis_total * 100)}%` : null} badgeColor="bg-emerald-100 text-emerald-700" />
                <DetailRow label="Devis refuses" value={data.devis_refuses} />
                <DetailRow label="Depenses totales" value={`CHF ${data.depenses_total.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}`} variation={data.depenses_variation} />
                <DetailRow label="Heures travaillees" value={`${data.heures_total}h`} />
                <DetailRow label="Nouveaux clients" value={data.nouveaux_clients} />
              </div>
            </Card>

            {/* Info */}
            <div className="flex items-center gap-2 text-xs text-slate-400 justify-center">
              <Calendar className="w-3.5 h-3.5" />
              <span>Ce rapport est envoye automatiquement chaque lundi a 07:00</span>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

const KpiCard = ({ icon: Icon, label, value, variation, subtext, accent = 'slate', isNegative }) => {
  const colors = {
    emerald: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400',
    blue: 'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400',
    red: 'bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400',
    slate: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
  };
  return (
    <Card className="p-4 rounded-xl">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors[accent]} mb-2`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className={`text-xl font-bold ${isNegative ? 'text-red-600' : 'text-slate-900 dark:text-white'}`}>{value}</p>
      <p className="text-[11px] text-slate-500 uppercase tracking-wide">{label}</p>
      {variation && (
        <p className={`text-xs mt-1 font-medium ${variation.startsWith('+') || variation.startsWith('-') ? (variation.startsWith('+') ? 'text-emerald-600' : 'text-red-600') : 'text-slate-400'}`}>
          {variation} vs sem. prec.
        </p>
      )}
      {subtext && <p className="text-xs mt-1 text-slate-400">{subtext}</p>}
    </Card>
  );
};

const DetailRow = ({ label, value, prev, variation, badge, badgeColor }) => (
  <div className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
    <span className="text-sm text-slate-600 dark:text-slate-400">{label}</span>
    <div className="flex items-center gap-3">
      {prev !== undefined && <span className="text-xs text-slate-400">{prev} prec.</span>}
      {variation && (
        <span className={`text-xs font-medium ${variation.startsWith('-') ? 'text-emerald-600' : variation.startsWith('+') ? 'text-red-600' : 'text-slate-400'}`}>
          {variation}
        </span>
      )}
      {badge && <Badge className={`text-[10px] ${badgeColor}`}>{badge}</Badge>}
      <span className="text-sm font-bold text-slate-900 dark:text-white">{value}</span>
    </div>
  </div>
);

export default WeeklyReportPage;
